import { useEffect, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { SignalRecommenderFile, SignalRecommenderFileStatus } from "../types/model";
import { signalRecommenderFileStatusMap } from "../types/model";
import signalRecommenderI18nKeys from "../utils/i18n";

import { useSignalRecommenderToasts } from "./useSignalRecommenderToasts";

/**
 * Announces enrichment jobs finishing, by watching the list for status transitions.
 *
 * Toasts are transient here rather than a persistent "processing" one: the row already carries progress,
 * and a toast that outlives the page it was started from can never resolve itself.
 */
export const useDealSignalStatusToasts = (files: readonly SignalRecommenderFile[] | undefined): void => {
    const { translate } = useTranslate();
    const { showSuccess, showError } = useSignalRecommenderToasts();

    /** Last status seen per file id. A missing entry means "never seen", which is deliberately silent. */
    const seenStatusesRef = useRef<Map<number, SignalRecommenderFileStatus> | null>(null);

    useEffect(() => {
        if (!files) {
            return;
        }

        seenStatusesRef.current ??= new Map();
        const seenStatuses = seenStatusesRef.current;

        files.forEach(({ id, status }) => {
            const previousStatus = seenStatuses.get(id);

            seenStatuses.set(id, status);

            // Only *leaving* an already-observed in-progress state is news. On a cold load nothing was
            // observed before, so files that finished before the page opened stay quiet — and a status
            // that has not moved since the last poll compares equal to itself and does too.
            if (previousStatus !== signalRecommenderFileStatusMap.inProgress) {
                return;
            }

            if (status === signalRecommenderFileStatusMap.succeeded) {
                showSuccess(translate(signalRecommenderI18nKeys.toast.ready));
            }

            if (status === signalRecommenderFileStatusMap.failed) {
                showError(translate(signalRecommenderI18nKeys.toast.error));
            }
        });
    }, [files, showSuccess, showError, translate]);
};
